import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, X } from 'lucide-react';
import { useMeditationPlayerStore } from '@/store/meditationPlayerStore';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Мини-плеер — фиксированная плашка над BottomNav
 * 
 * Показывается на ВСЕХ экранах когда играет музыка.
 * Скрывается:
 *  - когда юзер уже на странице полного плеера (/relax/player/...)
 *  - когда трек закрыт (isClosed)
 *  - когда нет трека (currentMeditation === null)
 */
export function MiniPlayer() {
  const nav = useNavigate();
  const location = useLocation();
  const { haptic } = useTelegram();
  
  const {
    currentMeditation,
    isPlaying,
    currentTime,
    duration,
    isClosed,
    play,
    pause,
    stop,
  } = useMeditationPlayerStore();
  
  // Не показываем если:
  // - нет трека
  // - трек закрыт юзером
  // - мы уже на странице полного плеера
  if (!currentMeditation || isClosed) return null;
  if (location.pathname.startsWith('/relax/player/')) return null;
  
  // На экранах дыхания (intro и run) — поднимаем плеер выше чтобы не закрывать кнопки
  const isBreathingScreen = location.pathname.startsWith('/breathing/');
  const bottomOffset = isBreathingScreen ? '140px' : '60px';
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  
  function togglePlay() {
    haptic('light');
    if (isPlaying) {
      pause();
    } else {
      play();
    }
  }
  
  function handleClose(e: React.MouseEvent) {
    e.stopPropagation();
    haptic('medium');
    stop();
  }
  
  function openFullPlayer() {
    haptic('light');
    nav(`/relax/player/${currentMeditation.slug}`);
  }
  
  return (
    <AnimatePresence>
      <motion.div
        key="mini-player"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className="fixed left-0 right-0 z-40 px-3 max-w-md mx-auto pointer-events-none"
        style={{ bottom: bottomOffset }}
      >
        <button
          type="button"
          onClick={openFullPlayer}
          className="w-full bg-bg-card border border-line rounded-2xl shadow-lg shadow-black/40 flex items-center gap-3 pl-3 pr-2 py-2.5 pointer-events-auto active:scale-[0.98] transition-transform relative overflow-hidden"
        >
          {/* Тонкая полоска прогресса сверху */}
          <div 
            className="absolute top-0 left-0 h-0.5 bg-emerald-glow transition-all"
            style={{ width: `${progress}%` }}
          />
          
          {/* Emoji/иконка трека */}
          <div className="w-10 h-10 rounded-xl bg-emerald-glow/10 flex items-center justify-center shrink-0 text-xl">
            {currentMeditation.emoji || '🎵'}
          </div>
          
          {/* Название */}
          <div className="flex-1 text-left min-w-0">
            <div className="text-[13px] font-medium text-ink truncate">
              {currentMeditation.title}
            </div>
            <div className="text-[10px] text-ink-dim truncate">
              {formatTime(currentTime)} / {formatTime(duration)}
            </div>
          </div>
          
          {/* Play/Pause */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="w-9 h-9 rounded-full bg-emerald-glow text-bg flex items-center justify-center shrink-0 active:scale-95 transition-transform"
            aria-label={isPlaying ? 'Пауза' : 'Играть'}
          >
            {isPlaying ? (
              <Pause size={14} className="fill-current" />
            ) : (
              <Play size={14} className="fill-current ml-0.5" />
            )}
          </button>
          
          {/* Закрыть */}
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-bg-elevated text-ink-dim flex items-center justify-center shrink-0 hover:text-danger active:scale-95 transition-all"
            aria-label="Закрыть"
          >
            <X size={14} />
          </button>
        </button>
      </motion.div>
    </AnimatePresence>
  );
}

function formatTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
